import React, { useEffect, useState } from 'react';
import { Gate } from './components/Gate';
import { WelcomeLetter } from './components/WelcomeLetter';
import { ProjectSearch } from './components/ProjectSearch';
import { BookGrid } from './components/BookGrid';
import { BonusStack } from './components/BonusStack';
import { UnlockStack } from './components/UnlockStack';
import { AgeLibraries } from './components/AgeLibraries';
import { ThreeSteps } from './components/ThreeSteps';
import { CommunityCard } from './components/CommunityCard';
import { TIER_LABEL } from './data/tiers';
import type { Tier } from './data/tiers';
const STORE_KEY = 'workshop-member';
interface Member {
  email: string;
  tier: Tier;
}
interface AppProps {
  startSignedIn?: boolean;
  startTier?: Tier;
}
export function App({
  startSignedIn = false,
  startTier = 'basic'
}: AppProps) {
  const [member, setMember] = useState<Member | null>(startSignedIn ? {
    email: 'member@example.com',
    tier: startTier
  } : null);
  useEffect(() => {
    if (startSignedIn) return;
    try {
      const saved = window.sessionStorage.getItem(STORE_KEY);
      if (!saved) return;
      /* Older sessions stored a bare email string. Read those too rather than
         throwing somebody back out to the gate mid-visit. */
      if (saved.startsWith('{')) {
        const parsed = JSON.parse(saved) as Member;
        if (parsed?.email) setMember({
          email: parsed.email,
          tier: parsed.tier ?? 'basic'
        });
      } else {
        setMember({
          email: saved,
          tier: 'basic'
        });
      }
    } catch {
      /* private windows block storage; the gate simply shows again */
    }
  }, [startSignedIn]);
  const signIn = (email: string, tier: Tier) => {
    const next = {
      email,
      tier
    };
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setMember(next);
  };
  const signOut = () => {
    try {
      window.sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setMember(null);
  };
  if (member === null) {
    return <Gate onUnlock={signIn} data-id="element-0" data-mp="356wxj89s6fx" />;
  }
  return <div className="blueprint min-h-screen w-full bg-ground" data-id="element-1" data-mp="3uct2xiigokzy">
      <header className="border-b-2 border-ink/10 bg-surface/70" data-id="element-2" data-mp="3ucjc5sple9t7">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-3 px-5 py-5 sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-3" data-mp="d4xlz5e29t8m">
          <div className="flex items-baseline gap-3" data-id="element-4" data-mp="86clr5yt79k6">
            <span className="font-display text-[19px] font-semibold text-ink" data-id="element-5" data-mp="3gkla21uate6j">Little Engineer</span>
            <span aria-hidden="true" className="h-3 w-px bg-rule" data-id="element-6" data-mp="3bn2ys4st6mvd" />
            <span className="smallcaps font-sans text-[10px] text-gold" data-id="element-7" data-mp="3qb7inc3ll4fq">
              {TIER_LABEL[member.tier]}
            </span>
          </div>
          <div className="flex items-baseline gap-4" data-id="element-8" data-mp="3ljfzvnqoo305">
            <span className="font-sans text-[12px] text-muted" data-id="element-9" data-mp="2xuwsdafgju94">{member.email}</span>
            <button type="button" onClick={signOut} className="font-sans text-[12px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-10" data-mp="2xsk2mb3gkfli">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[76rem] px-5 pb-24 pt-14 sm:px-8 sm:pt-20" data-id="element-11" data-mp="13e54li23pr2w">
        <div className="flex flex-col gap-16 sm:gap-24" data-id="element-12" data-mp="1r496nkzn5irs">
          <WelcomeLetter data-id="element-13" data-mp="1r4seg8vkuqs3" />
          <BookGrid data-id="element-14" data-mp="1r2g0s7wur2ku" />
          <ProjectSearch data-id="element-15" data-mp="1w33ookpsb3au" />

          <div className="flex items-center gap-5" aria-hidden="true" data-id="element-16" data-mp="1w2asgdogbw6y">
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-17" data-mp="1i3uimdvalpkl" />
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-18" data-mp="1i3b4l88rjlbu">
              That was the collection. Now the extras
            </span>
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-19" data-mp="6nmbs08tvs34" />
          </div>

          <BonusStack data-id="element-20" data-mp="aq9a8k008b9a" />
          <UnlockStack tier={member.tier} data-id="element-21" data-mp="aqslxiz2a3ev" />
          <AgeLibraries data-id="element-22" data-mp="qx5hw2u8iwcl" />
          <ThreeSteps data-id="element-23" data-mp="qxf89rj6z5it" />
          <CommunityCard memberEmail={member.email} data-id="element-24" data-mp="qxxcbac3io1a" />
        </div>
      </main>

      <footer className="border-t border-rule bg-surface/60" data-id="element-25" data-mp="m65hgnlwpw5c">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-2 px-5 py-8 font-sans text-[13px] text-muted sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-26" data-mp="h7c4qxeg2968">
          <p data-id="element-27" data-mp="ca2j7su6ehno">Little Engineer Mega Kit · The Workshop</p>
          <p data-id="element-28" data-mp="19wjbukrys733">Files are for your household. Please do not share the link.</p>
        </div>
      </footer>
    </div>;
}